import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getProducts } from '../api/productApi';
import ProductCard from '../components/ProductCard';
import '../styles/Home.css';

const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      title: 'Welcome to OneMart',
      subtitle: 'Discover Amazing Products',
      description: 'Shop the latest trends at unbeatable prices',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&h=600&fit=crop',
      cta: 'Shop Now'
    },
    {
      id: 2,
      title: 'New Arrivals',
      subtitle: 'Fresh Collection 2024',
      description: 'Explore our newest products and exclusive deals',
      image: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=1200&h=600&fit=crop',
      cta: 'Explore Now'
    },
    {
      id: 3,
      title: 'Special Offers',
      subtitle: 'Up to 50% Off',
      description: 'Limited time deals on selected items',
      image: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1200&h=600&fit=crop',
      cta: 'Shop Deals'
    }
  ];

  useEffect(() => {
    fetchProducts();
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const fetchProducts = async () => {
    try {
      const data = await getProducts();
      if (!data || data.length === 0) {
        setError('No products found');
        setProducts([]);
      } else {
        // Show only 9 products on home page
        setProducts(data.slice(0, 9));
        setError(null);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to load products. Please try again later.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="home">
      {/* Hero Slider Section */}
      <section className="hero-slider">
        <div className="slider-container">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`slide ${index === currentSlide ? 'active' : ''}`}
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="slide-overlay"></div>
              <div className="slide-content">
                <h1>{slide.title}</h1>
                <h2>{slide.subtitle}</h2>
                <p>{slide.description}</p>
                <Link to="/products" className="btn-hero">{slide.cta}</Link>
              </div>
            </div>
          ))}
          
          <button className="slider-btn prev" onClick={prevSlide}>❮</button>
          <button className="slider-btn next" onClick={nextSlide}>❯</button>
          
          <div className="slider-dots">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="home-about">
        <div className="about-container">
          <div className="about-content">
            <h2>About OneMart</h2>
            <p>
              Welcome to OneMart, your premier destination for quality products and exceptional service. 
              We're committed to bringing you the best shopping experience with a wide selection of products, 
              competitive prices, and outstanding customer support.
            </p>
            <p>
              Our mission is to make online shopping easy, secure, and enjoyable for everyone. 
              With fast shipping, secure payments, and 24/7 customer support, we're here to serve you.
            </p>
            <Link to="/about" className="btn-learn-more">Learn More About Us</Link>
          </div>
          <div className="about-features">
            <div className="feature-box">
              <div className="feature-icon">🚚</div>
              <h3>Fast Shipping</h3>
              <p>Quick delivery to your doorstep</p>
            </div>
            <div className="feature-box">
              <div className="feature-icon">💳</div>
              <h3>Secure Payment</h3>
              <p>Safe and encrypted transactions</p>
            </div>
            <div className="feature-box">
              <div className="feature-icon">🔄</div>
              <h3>Easy Returns</h3>
              <p>Hassle-free return policy</p>
            </div>
            <div className="feature-box">
              <div className="feature-icon">💬</div>
              <h3>24/7 Support</h3>
              <p>Always here to help you</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="featured-products">
        <h2>Featured Products</h2>
        <p className="section-subtitle">Discover our handpicked selection of amazing products</p>
        {loading ? (
          <p className="loading-text">Loading products...</p>
        ) : error ? (
          <p className="error-text">{error}</p>
        ) : (
          <div className="products-grid">
            {products.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
        <Link to="/products" className="btn-view-all">View All Products</Link>
      </section>
    </div>
  );
};

export default Home;
